// A filter criterion: one field of `GameFilter`, its URL query parameter,
// how a game is matched against it, and how the library's filter bar offers
// it. The criteria are listed in `criteria/index.ts`; parsing, formatting,
// matching and the filter bar are all driven by that list, so a new
// criterion is a new file there plus one line in the list.
import type { FilterableGame, GameFilter } from '@chess/api-model';

/** A field a criterion owns: every field of the filter but the engine's own `ids`. */
export type FilterKey = Exclude<keyof GameFilter, 'ids'>;
/** One value of a field: a field taking several (`OneOrMore`) is matched one value at a time. */
type ItemOf<T> = T extends readonly (infer U)[] ? U : T;
export type FilterItem<K extends FilterKey> = ItemOf<NonNullable<GameFilter[K]>>;

/** One choice in a control: its value, its label, and the heading it sits under in the menu, if any. */
export type FilterOption<V> = readonly [value: V, label: string, group?: string];

/** A menu of checkboxes: any number of values, a game matching any of them passes. */
export interface ChoiceControl<V> {
  kind: 'choice';
  label: string;
  options: ReadonlyArray<FilterOption<V>>;
  /** A value reached only by a link (not among `options`): its label, listed while chosen. */
  linkedLabel?(value: V): string | undefined;
}

/** A search field, written after a short pause. */
export interface TextControl {
  kind: 'text';
  label: string;
  placeholder: string;
  /** The value the field shows; the criterion's own field by default. */
  read?(filter: GameFilter): string | undefined;
  /** The filter after the field sets a value; the criterion's own field by default. */
  write?(filter: GameFilter, value: string | undefined): GameFilter;
  /** Values to suggest while typing, from the games the bar filters, most useful first. */
  suggest?(games: readonly FilterableGame[]): string[];
}

export type FilterControl<V> = (ChoiceControl<V> | TextControl) & {
  /** Where the control sits in the filter bar, ascending. */
  order: number;
  /** Shown only under "More filters" (or while set). */
  more?: boolean;
};

export interface FilterCriterion<K extends FilterKey = FilterKey> {
  readonly key: K;
  /** The URL query parameter; links into the library use it. Several values are comma-separated. */
  readonly query: string;
  /** Whether the field takes several values (`OneOrMore`); a choice control requires it. */
  readonly many?: boolean;
  /** One value from the query parameter's text (trimmed, never empty); undefined to ignore it. */
  parse(raw: string): FilterItem<K> | undefined;
  /** One value's query parameter text, or undefined to leave it out. */
  format(value: FilterItem<K>): string | undefined;
  /** Whether a game passes one value of this criterion; `filter` is the whole filter, `now` ends any period. */
  matches(game: FilterableGame, value: FilterItem<K>, filter: GameFilter, now: number): boolean;
  /** How the filter bar offers it; none for a criterion only links set. */
  readonly control?: FilterControl<FilterItem<K>>;
}

/** The parse for a value from a fixed list. */
export const oneOf =
  <T extends string>(allowed: readonly T[]) =>
  (raw: string): T | undefined =>
    (allowed as readonly string[]).includes(raw) ? (raw as T) : undefined;

/** The parse and format for free text: trimmed, empty is none. */
export const text = {
  parse: (raw: string): string | undefined => raw.trim() || undefined,
  format: (value: string): string | undefined => value.trim() || undefined,
};
